import { useEffect } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';

import { composeValidators } from 'CorePackage/MainModule/common/composeValidators';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { Modal } from 'CorePackage/MainModule/components/Modal';
import { DATE_FORMAT_LONG } from 'CorePackage/MainModule/constants/dateFormat';
import { federalLawsSelectOptions } from 'CorePackage/MainModule/constants/federalLawsSelectOptions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ISelectOption } from 'CorePackage/MainModule/interfaces/ISelectOption';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/makeMaxCharactersValidator';
import { makeMinCharactersValidator } from 'CorePackage/MainModule/validators/makeMinCharactersValidator';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/makeNoLaterThenValidator';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/makeNoSoonerThenValidator';
import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { captchaSetActionAfterTokenLoad, captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';

import { SearchFiltersOptionTypesEnum } from '../enums/SearchFiltersOptionTypesEnum';
import { IKnmSearchInitFilter } from '../interfaces/IKnmSearchInitFilter';
import styles from './SearchFiltersModal.module.scss';
import { searchGetFilterOptions, searchGetResultData, searchSetFilter } from '../actions/searchActions';
import { getSearchFilter, getSearchFilterOptionByType } from '../selectors/searchSelectors';

interface IFiltersModalStoreProps {
  filterValues: Partial<IKnmSearchInitFilter>;
  typeSubjectKNMOptions?: ISelectOption<number>[];
  typeKNMOptions?: ISelectOption<number>[];
  yearKNMOptions?: ISelectOption<number>[];
  typeOfStateSupervisionOptions?: ISelectOption<number>[];
  monthKNMOptions?: ISelectOption<number>[];
  statusKNMOptions?: ISelectOption<number>[];
  captchaToken: string | null;
}

interface IFiltersModalProps {
  dialog: boolean;
  openDialog: (value: boolean) => void;
}

const FiltersModalWithoutStore = ({
  dialog,
  openDialog,
  filterValues,
  typeSubjectKNMOptions,
  typeKNMOptions,
  yearKNMOptions,
  typeOfStateSupervisionOptions,
  monthKNMOptions,
  statusKNMOptions,
  captchaToken,
}: IFiltersModalProps & IFiltersModalStoreProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(searchGetFilterOptions());
  }, [dispatch]);

  const onSubmit = (values: Partial<IKnmSearchInitFilter>) => {
    dispatch(searchSetFilter(values ?? {}));
    if (!captchaToken) {
      dispatch(captchaSetActionAfterTokenLoad(searchGetResultData));
      dispatch(captchaSetIsActive(true));
    } else {
      dispatch(searchGetResultData());
    }
    openDialog(false);
  };

  const validateForm = (values: Partial<IKnmSearchInitFilter>) => {
    const validateDatePicker = (fromDate?: string, toDate?: string, startDate?: boolean) => {
      if (
        (toDate || fromDate) &&
        (!formatDate({ date: toDate || '', dateFormat: DATE_FORMAT_LONG }) ||
          !formatDate({ date: fromDate || '', dateFormat: DATE_FORMAT_LONG }))
      ) {
        return 'Некорректный период';
      }
      if (startDate && toDate && fromDate)
        return makeNoLaterThenValidator(
          'Дата начала периода должна быть меньше или равна дате окончания',
          () => toDate,
        )(fromDate);

      if (fromDate && toDate)
        return makeNoSoonerThenValidator(
          'Дата окончания периода должна быть больше или равна дате начала',
          () => fromDate,
        )(toDate);

      if ((toDate && !fromDate && startDate) || (fromDate && !toDate && !startDate)) return 'Поле не может быть пустым';
    };
    return {
      fromDate: validateDatePicker(values?.fromDate, values?.toDate, true),
      toDate: validateDatePicker(values?.fromDate, values?.toDate),
    };
  };

  const renderFiltersForm = ({ handleSubmit }: FormRenderProps<Partial<IKnmSearchInitFilter>>) => {
    return (
      <form onSubmit={handleSubmit}>
        <div className={styles.Content}>
          <div className={styles.ContentInner}>
            <div className={cn(styles.Field, styles.FieldFrguOrgName)}>
              <label htmlFor="federalLaws">Федеральный закон</label>
              <SelectInputField name="federalLaws" options={federalLawsSelectOptions} isMulti />
            </div>
            <div className={cn(styles.Field, styles.FieldFrguOrgName)}>
              <label htmlFor="frguOrgName">Наименование органа контроля (надзора)</label>
              <TextInputField
                name="frguOrgName"
                validate={composeValidators(
                  makeMinCharactersValidator(3, 'Минимальная длина строки не соответствует требуемому значению'),
                  makeMaxCharactersValidator(1025, 'Превышена допустимая длина строки'),
                )}
              />
            </div>
            <div className={cn(styles.Field, styles.FieldProsecutorName)}>
              <label htmlFor="prosecutorName">Наименование органа прокуратуры</label>
              <TextInputField
                name="prosecutorName"
                validate={composeValidators(
                  makeMinCharactersValidator(3, 'Минимальная длина строки не соответствует требуемому значению'),
                  makeMaxCharactersValidator(129, 'Превышена допустимая длина строки'),
                )}
              />
            </div>

            <div className={cn(styles.Field, styles.FieldSubjectType)}>
              <label htmlFor="subjectType">Тип субъекта КНМ</label>
              <SelectInputField name="subjectType" options={typeSubjectKNMOptions} className={styles.Field} isMulti />
            </div>
            <div className={cn(styles.Field, styles.FieldKnmType)}>
              <label htmlFor="knmType">Характер КНМ</label>
              <SelectInputField name="knmType" options={typeKNMOptions} className={styles.Field} isMulti />
            </div>
            <div className={cn(styles.Field, styles.FieldYear)}>
              <label htmlFor="year">Год проведения КНМ</label>
              <SelectInputField name="year" id="year" options={yearKNMOptions} className={styles.Field} isMulti />
            </div>
            <div className={cn(styles.Field, styles.FieldSupervisionName)}>
              <label htmlFor="supervisionName">Вид государственного контроля (надзора)</label>
              <SelectInputField name="supervisionName" options={typeOfStateSupervisionOptions} isSearchable />
            </div>
            <div className={cn(styles.Field, styles.FieldOrganizationName)}>
              <label htmlFor="organizationName">Наименование проверяемого лица</label>
              <TextInputField
                name="organizationName"
                validate={composeValidators(
                  makeMinCharactersValidator(3, 'Минимальная длина строки не соответствует требуемому значению'),
                  makeMaxCharactersValidator(1025, 'Превышена допустимая длина строки'),
                )}
              />
            </div>
            <div className={cn(styles.Field, styles.FieldDates)}>
              <label>Период проведения</label>
              <div className={cn(styles.FormGroup, styles.DatePickers)}>
                <div className={styles.DatePicker}>
                  <DatePickerField opensOnTop name="fromDate" />
                </div>
                <div className={styles.DateSeparator}>&nbsp;-&nbsp;</div>
                <div className={styles.DatePicker}>
                  <DatePickerField opensOnTop name="toDate" />
                </div>
              </div>
            </div>
            <div className={cn(styles.Field, styles.FieldStartMonth, 'custom-dropdown-position')}>
              <label htmlFor="startMonth">Месяц даты начала проведения КНМ</label>
              <SelectInputField isMulti name="startMonth" options={monthKNMOptions} className={styles.Field} />
            </div>
            <div className={cn(styles.Field, styles.FieldErpId)}>
              <label htmlFor="erpId">Учетный номер КНМ</label>
              <TextInputField
                name="erpId"
                className={styles.Field}
                validate={makeMaxCharactersValidator(73, 'Превышена допустимая длина строки')}
              />
            </div>
            <div className={cn(styles.Field, styles.FieldInn)}>
              <label htmlFor="inn">ИНН</label>
              <TextInputField
                name="inn"
                className={styles.Field}
                validate={makeMaxCharactersValidator(65, 'Превышена допустимая длина строки')}
              />
            </div>
            <div className={cn(styles.Field, styles.FieldOgrn)}>
              <label htmlFor="ogrn">ОГРН</label>
              <TextInputField
                name="ogrn"
                className={styles.Field}
                validate={makeMaxCharactersValidator(65, 'Превышена допустимая длина строки')}
              />
            </div>
            <div className={cn(styles.Field, styles.FieldStatus, 'custom-dropdown-position')}>
              <label htmlFor="status">Статус КНМ</label>
              <SelectInputField isMulti opensOnTop name="status" options={statusKNMOptions} />
            </div>
          </div>
        </div>
        <div className={styles.Footer}>
          <Button className={styles.SubmitButton} color="primary" type="submit" text="Применить" />
        </div>
      </form>
    );
  };

  const handleCloseModal = () => openDialog(false);

  return (
    <Modal visible={dialog} modalBodyClassName={styles.Body} onClose={handleCloseModal}>
      <div className={styles.Header}>
        <h5 className={styles.Title}>Фильтры</h5>
      </div>
      <Form<Partial<IKnmSearchInitFilter>>
        initialValues={filterValues}
        render={renderFiltersForm}
        onSubmit={onSubmit}
        validate={validateForm}
      />
    </Modal>
  );
};

export const SearchFiltersModal = connect((state: IRootState) => ({
  filterValues: getSearchFilter(state),
  typeSubjectKNMOptions: getSearchFilterOptionByType(state, SearchFiltersOptionTypesEnum.TYPE_SUBJECT_KNM),
  typeKNMOptions: getSearchFilterOptionByType(state, SearchFiltersOptionTypesEnum.TYPE_KNM),
  yearKNMOptions: getSearchFilterOptionByType(state, SearchFiltersOptionTypesEnum.YEAR_KNM),
  typeOfStateSupervisionOptions: getSearchFilterOptionByType(
    state,
    SearchFiltersOptionTypesEnum.TYPE_OF_STATE_SUPERVISION,
  ),
  monthKNMOptions: getSearchFilterOptionByType(state, SearchFiltersOptionTypesEnum.MONTH_KNM),
  statusKNMOptions: getSearchFilterOptionByType(state, SearchFiltersOptionTypesEnum.STATUS_KNM),
  captchaToken: getCaptchaToken(state),
}))(FiltersModalWithoutStore);
